import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { FileText, Loader2, RotateCcw } from 'lucide-react';

/**
 * #12 — "Prompts del sistema" admin section.
 *
 * Lists the backend prompt catalog (/admin/prompts): each prompt shows its key,
 * description and an editable textarea bound to the CURRENT text (override or
 * default). Saving PUTs the override; "Restaurar default" PUTs an empty text so
 * the backend clears the override and falls back to the hardcoded default.
 *
 * Editing here changes the REAL AI calls, so the card warns about that. Shared by
 * the root and tiptap AdminPanels (self-contained; reads/writes /admin/prompts).
 */
const PromptsCard = () => {
  const [prompts, setPrompts] = useState([]);
  const [drafts, setDrafts] = useState({});     // key -> edited text
  const [busy, setBusy] = useState('');          // key currently saving/resetting

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/prompts');
      const list = res.data || [];
      setPrompts(list);
      setDrafts(Object.fromEntries(list.map((p) => [p.key, p.current])));
    } catch (e) {
      if (e.response?.status === 403) toast.error('Solo administradores');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setBusy(key);
    try {
      await axiosInstance.put('/admin/prompts', { key, text: drafts[key] ?? '' });
      toast.success('Prompt guardado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el prompt');
    } finally {
      setBusy('');
    }
  };

  const reset = async (key) => {
    setBusy(key);
    try {
      await axiosInstance.put('/admin/prompts', { key, text: '' });
      toast.success('Prompt restaurado al valor por defecto');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al restaurar');
    } finally {
      setBusy('');
    }
  };

  return (
    <details className="glass rounded-xl p-6" open>
      <summary className="cursor-pointer select-none text-sm font-medium text-slate-700 dark:text-slate-200">
        <span className="inline-flex items-center gap-2 align-middle">
          <FileText className="w-4 h-4 text-purple-600" />
          Prompts del sistema
        </span>
      </summary>
      <div className="mt-4 space-y-4">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Editar estos textos afecta las llamadas REALES a la IA (chat, herramientas,
          comentarios). Conserva los placeholders entre llaves (p. ej. <code>{'{body}'}</code>,{' '}
          <code>{'{anchor}'}</code>, <code>{'{listed}'}</code>). Deja vacío y guarda para
          volver al valor por defecto.
        </p>

        {prompts.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Cargando prompts…</p>
        )}

        <div className="space-y-4">
        {prompts.map((p) => {
          const dirty = (drafts[p.key] ?? '') !== p.current;
          const overridden = p.current !== p.default;
          return (
            <div key={p.key} data-testid={`admin-prompt-${p.key}`} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-slate-700 dark:text-slate-200 truncate">{p.key}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{p.description}</div>
                </div>
                {overridden && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800 shrink-0">
                    Editado
                  </Badge>
                )}
              </div>
              <Textarea
                data-testid={`admin-prompt-input-${p.key}`}
                value={drafts[p.key] ?? ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [p.key]: e.target.value }))}
                rows={6}
                className="resize-y font-mono text-xs"
              />
              <div className="flex gap-2">
                <Button
                  data-testid={`admin-prompt-save-${p.key}`}
                  size="sm"
                  onClick={() => save(p.key)}
                  disabled={busy === p.key || !dirty}
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                >
                  {busy === p.key ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Guardar
                </Button>
                <Button
                  data-testid={`admin-prompt-reset-${p.key}`}
                  size="sm"
                  variant="secondary"
                  onClick={() => reset(p.key)}
                  disabled={busy === p.key || !overridden}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restaurar default
                </Button>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </details>
  );
};

export default PromptsCard;
